import SocketCentrifugeBase, { SubscriptionError } from '@/api/sockets/SocketCentrifugeBase';
import { Callback, Dict } from '@/types/utility';

export enum SampleSocketEvents {
  SAMPLE_REFRESH = 'sample.refresh',
}

enum SampleSocketRPC {
  GET = 'sample.get',
  ARCHIVE = 'sample.archive',
  RESTORE = 'sample.restore',
  UPDATE = 'sample.update',
  SHARE = 'sample.share',
  STOP_SHARING = 'sample.stopSharing',
}

export default class SampleSocket extends SocketCentrifugeBase<
  SampleSocketEvents,
  SampleSocketRPC
> {
  private static connections: Dict<SampleSocket> = {};

  protected listeners: Record<SampleSocketEvents, Callback[]> = {
    [SampleSocketEvents.SAMPLE_REFRESH]: [],
  };

  private constructor(
    public id: number,
    onConnect?: Callback<[SocketCentrifugeBase<SampleSocketEvents, SampleSocketRPC>]>,
    onError?: Callback<[SubscriptionError]>,
  ) {
    super(`personal:sample-${id}`, {
      handleEvents: ({ data }) => {
        switch (data.event) {
          case SampleSocketEvents.SAMPLE_REFRESH: {
            this.callListeners(SampleSocketEvents.SAMPLE_REFRESH, data.sample);
            break;
          }
        }
      },
      onConnect,
      onError,
    });

    this.dataAddedToEveryRequest = { sample_id: this.id };
  }

  public static start(
    id: number,
    onConnect?: Callback<[SocketCentrifugeBase<SampleSocketEvents, SampleSocketRPC>]>,
    onError?: Callback<[SubscriptionError]>,
  ) {
    const connection = SampleSocket.connections[id];
    if (connection) {
      connection.addSession();
      onConnect?.(connection);
      return connection;
    }

    SampleSocket.connections[id] = new SampleSocket(id, onConnect, onError);
    return SampleSocket.connections[id];
  }

  close(listenersGroupId: string) {
    const isClosed = super.close(listenersGroupId);

    if (super.close()) {
      delete SampleSocket.connections[this.id];
    }

    return isClosed;
  }

  get() {
    return this.sendNamedRPC(SampleSocketRPC.GET);
  }

  archive() {
    return this.sendNamedRPC(SampleSocketRPC.ARCHIVE);
  }

  restore() {
    return this.sendNamedRPC(SampleSocketRPC.RESTORE);
  }

  update(sample) {
    return this.sendNamedRPC(SampleSocketRPC.UPDATE, { sample });
  }

  share() {
    return this.sendNamedRPC(SampleSocketRPC.SHARE);
  }

  stopSharing() {
    return this.sendNamedRPC(SampleSocketRPC.STOP_SHARING);
  }
}
